// @ts-nocheck
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Download, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { AffiliateDetailsSheet } from '@/components/admin/AffiliateDetailsSheet';

interface AffiliatePayout {
  id: string;
  name: string;
  email: string;
  referral_code: string;
  commission_rate: number;
  status: string;
  stripe_account_status: string | null;
  conversions: number;
  connections: number;
  earnings: number;
}

const AffiliatePayoutsTab: React.FC = () => {
  const [selectedAffiliates, setSelectedAffiliates] = useState<string[]>([]);
  const [selectedAffiliateForDetails, setSelectedAffiliateForDetails] = useState<{
    id: string;
    name: string;
    email: string;
  } | null>(null);
  const [detailsSheetOpen, setDetailsSheetOpen] = useState(false);
  
  const { data: affiliates, isLoading } = useQuery({
    queryKey: ['affiliate-payouts'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_affiliate_payouts');

      if (error) throw error;

      console.log('✅ Active affiliates loaded:', data?.length || 0);
      console.log('Affiliate data:', data);
      
      return (data || []) as AffiliatePayout[];
    },
  });

  const exportToCSV = () => {
    if (!affiliates) return;

    const headers = ['Affiliate Name', 'Email', 'Status', 'Commission Rate', 'Referral Code', 'Athletes Connected', 'Athletes Converted', 'Earnings', 'Last Paid', 'W-9 Status'];
    const rows = affiliates.map(a => [
      a.name,
      a.email,
      a.status,
      `${Number(a.commission_rate)}%`,
      a.referral_code,
      a.connections,
      a.conversions,
      `$${Number(a.earnings).toFixed(2)}`,
      'N/A',
      a.stripe_account_status ? 'Submitted' : 'Pending',
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `affiliate-payouts-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    
    toast.success('Exported to CSV');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading affiliate payouts...</div>;
  }

  const totalPending = affiliates?.reduce((sum, a) => sum + Number(a.earnings), 0) || 0;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Commissions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalPending)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Active Affiliates</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{affiliates?.length || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Missing W-9 Forms</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {affiliates?.filter(a => !a.stripe_account_status).length || 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Affiliate Payouts Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle>Affiliate Commissions</CardTitle>
              <CardDescription>Manage affiliate commission payouts</CardDescription>
            </div>
            <Button variant="outline" onClick={exportToCSV}>
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox />
                </TableHead>
                <TableHead>Affiliate</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Commission Rate</TableHead>
                <TableHead>Referral Code</TableHead>
                <TableHead className="text-center">Athletes Connected</TableHead>
                <TableHead className="text-center">Athletes Converted</TableHead>
                <TableHead className="text-right">Earnings</TableHead>
                <TableHead>Last Paid</TableHead>
                <TableHead>W-9 Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {affiliates?.map((affiliate) => (
                <TableRow key={affiliate.id}>
                  <TableCell>
                    <Checkbox
                      checked={selectedAffiliates.includes(affiliate.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedAffiliates([...selectedAffiliates, affiliate.id]);
                        } else {
                          setSelectedAffiliates(selectedAffiliates.filter(id => id !== affiliate.id));
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell className="font-medium">{affiliate.name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{affiliate.email}</TableCell>
                  <TableCell>
                    <Badge variant={affiliate.status === 'active' ? 'default' : 'secondary'}>
                      {affiliate.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">{Number(affiliate.commission_rate)}%</TableCell>
                  <TableCell className="font-mono text-sm">{affiliate.referral_code}</TableCell>
                  <TableCell className="text-center">
                    <span className={affiliate.connections > 0 && affiliate.conversions === 0 ? 'text-yellow-600' : ''}>
                      {affiliate.connections}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">{affiliate.conversions}</TableCell>
                  <TableCell className="text-right font-semibold">{formatCurrency(Number(affiliate.earnings))}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">N/A</TableCell>
                  <TableCell>
                    <Badge variant={affiliate.stripe_account_status ? 'default' : 'secondary'} className={affiliate.stripe_account_status ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                      {affiliate.stripe_account_status ? 'Submitted' : 'Pending'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedAffiliateForDetails({
                            id: affiliate.id,
                            name: affiliate.name,
                            email: affiliate.email,
                          });
                          setDetailsSheetOpen(true);
                        }}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                      <Button 
                        size="sm" 
                        onClick={() => toast.success('Payout processed')}
                        disabled={!affiliate.stripe_account_status}
                      >
                        Process
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Affiliate Details Sheet */}
      <AffiliateDetailsSheet
        affiliateId={selectedAffiliateForDetails?.id || null}
        affiliateName={selectedAffiliateForDetails?.name || ''}
        affiliateEmail={selectedAffiliateForDetails?.email || ''}
        open={detailsSheetOpen}
        onOpenChange={setDetailsSheetOpen}
      />
    </div>
  );
};

export default AffiliatePayoutsTab;
